import {getSession} from '@github-ui/alive'
import {focusManager, type QuerySubscriptionContext} from '@github-ui/react-query'
import {Throttler} from '@tanstack/react-pacer'
import {toQueryFilters, type QueryKeyOrFilters} from './utils'
import type {AliveUpdateData} from './use-invalidate-on-alive-update'
import {connectAliveSubscription} from '@github-ui/alive/connect-alive-subscription'

export interface InvalidateOnAliveSubscriptionConfig<TData extends AliveUpdateData = AliveUpdateData> {
  /** Override query key or filters for queries to invalidate. @default context.queryKey */
  queryKey?: QueryKeyOrFilters
  /** Optional filter for events; return `false` to ignore an event. */
  filter?: (data: TData) => boolean
  /** Throttle timeout to limit frequency of invalidations. @default 2000 */
  throttleTimeout?: number
}

/**
 * Query `subscription` which invalidates a query when a live update is received. Has built-in throttling, `wait`
 * delays, active window detection, and event filtering support.
 *
 * Use with query options `subscription` field. By default, will invalidate the current query. This can be customized
 * with `options.queryKey`.
 *
 * ```ts
 * useQuery({
 *   // ...
 *   subscription: (data, context) => invalidateOnAliveSubscription(data.aliveChannel, context)
 * })
 * ```
 *
 * @param channel Channel to subscribe to.
 * @param context Subscription context from the query callback.
 * @param config Config options.
 */
export async function invalidateOnAliveSubscription<TData extends AliveUpdateData = AliveUpdateData>(
  channel: string | null | undefined,
  context: QuerySubscriptionContext,
  config: InvalidateOnAliveSubscriptionConfig<TData> = {},
) {
  const session = await getSession()
  if (!session || !channel) return

  const invalidateThrottler = new Throttler(
    () => context.client.invalidateQueries(toQueryFilters(config.queryKey ?? context.queryKey)),
    {
      wait: config.throttleTimeout ?? 2000,
      // Trailing ensures that we show the latest data if a subsequent update occurs during the throttle period.
      trailing: true,
      // Trailing-only would be sufficient from a correctness standpoint but would artificially delay every update (by
      // the throttle timeout in addition to any event `wait`), so we use leading _and_ trailing at the cost of possible
      // extra requests (if two requests happen quickly, they'll each cause an invalidation but it will be spaced out
      // by `wait`). This is acceptable here where it wasn't in `useInvalidateOnAliveUpdate` because with this new
      // approach we will only ever have one active subscription, not many subscriptions hammering the same throttler.
      leading: true,
    },
  )

  let unsubscribeFocusListener: (() => void) | undefined = undefined

  // Even though `invalidate` will only refetch active queries, mounted queries in background tabs are still considered
  // active. Normally we could just set refetchType: none in this case and rely on auto-refetching stale queries on
  // window focus, but at GitHub we have `refetchOnWindowFocus` set to false globally. So we need to roll our own here:
  const invalidateWhenFocused = () => {
    // Note 'focus' here is not traditional element focus; in Tanstack Query terms it refers to an active window.
    if (focusManager.isFocused()) {
      invalidateThrottler.maybeExecute()
      return
    }

    unsubscribeFocusListener ??= focusManager.subscribe(focused => {
      if (focused) {
        invalidateThrottler.maybeExecute()
        unsubscribeFocusListener?.()
        unsubscribeFocusListener = undefined
      }
    })
  }

  const pendingTimeouts = new Set<ReturnType<typeof setTimeout>>()

  const aliveSubscription = connectAliveSubscription<TData>(session, channel, data => {
    const shouldHandle = config.filter?.(data) ?? true
    if (!shouldHandle) return

    if (data.wait !== undefined) {
      const timeout = setTimeout(() => {
        invalidateWhenFocused()
        pendingTimeouts.delete(timeout)
      }, data.wait)
      pendingTimeouts.add(timeout)
    } else {
      invalidateWhenFocused()
    }
  })

  return () => {
    aliveSubscription.unsubscribe()
    for (const timeout of pendingTimeouts) clearTimeout(timeout)
    unsubscribeFocusListener?.()
    invalidateThrottler.cancel()
  }
}
